import { CUSTOM_ELEMENTS_SCHEMA, OnInit, Component, DestroyRef, inject } from '@angular/core';
import { Subscription, SubscriptionsService } from '../services/user/subscriptions.service';
import { Content } from "../services/admin/content.service";
import { RouterLink, Router } from '@angular/router';
import { UtilsPipe } from '../utils.pipe';
import { CommonModule, DatePipe } from '@angular/common';
import { actionsIconConfig, basicTableActionTypes } from '../../constants';
import { utcToCentralDisplay } from '../utils/timezone-utils';
import { CloudWatchRumService } from '../services/cloudwatch-rum.service';
import { AuthContextService } from '../services/auth-context.service';
import { LoadingComponent } from '../loading/loading.component';
import { RunStatusPipe, runStatusOf, runStatusView } from './run-status.pipe';

/** While a run is in progress the list refreshes itself this often, for at most RUN_REFRESH_MAX_MS (ARAP-1910) */
export const RUN_REFRESH_MS = 20000;
export const RUN_REFRESH_MAX_MS = 15 * 60 * 1000;

@Component({
  selector: 'app-subscriptions',
  standalone: true,
  imports: [RouterLink, UtilsPipe, DatePipe, CommonModule, LoadingComponent, RunStatusPipe],
  templateUrl: './subscriptions.component.html',
  styleUrls: ['./subscriptions.component.css', './run-status-table.css'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SubscriptionsComponent implements OnInit {
  // Setting the list also checks for runs in progress, to keep their status fresh (ARAP-1910)
  private subscriptionList: Subscription[] = [];
  get subscriptions(): Subscription[] {
    return this.subscriptionList;
  }
  set subscriptions(next: Subscription[]) {
    const previous = this.subscriptionList;
    this.subscriptionList = next || [];
    this.announceFinishedRuns(previous, this.subscriptionList);
    this.scheduleRunRefresh();
  }
  basicTableActionTypes = basicTableActionTypes;
  actionsIconConfig = actionsIconConfig;
  isDeleteSubscriptionModalVisible = false;
  isNotificationModalVisible = false;
  subscriptionToDelete: Subscription | null = null;
  isLoading = false;
  alertMessage: string = '';
  alertVariant: 'error' | 'success' | 'warning' | 'info' = 'error';
  private cloudWatchRumService: CloudWatchRumService = inject(CloudWatchRumService);
  private authContextService: AuthContextService = inject(AuthContextService);

  constructor(private SubscriptionsService: SubscriptionsService, private router: Router) { }

  closeNotificationModal() {
    this.isNotificationModalVisible = false;
    this.alertMessage = '';
  }

  private showNotification(message: string, variant: 'success' | 'error') {
    this.alertVariant = variant;
    this.alertMessage = message;
    this.isNotificationModalVisible = true;
  }

  /** Read out to screen readers when a run that was in progress finishes */
  runStatusAnnouncement = '';
  /** True while the list refreshes itself because a run is in progress */
  isRefreshingRuns = false;
  private runRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private runRefreshSince: number | null = null;
  private readonly stopRunRefreshOnDestroy = inject(DestroyRef).onDestroy(() => this.stopRunRefresh());

  private scheduleRunRefresh(): void {
    this.stopRunRefresh();
    if (!this.subscriptionList.some(sub => runStatusOf(sub) === 'RUNNING')) {
      this.runRefreshSince = null;
      return;
    }
    const now = Date.now();
    this.runRefreshSince = this.runRefreshSince ?? now;
    if (now - this.runRefreshSince >= RUN_REFRESH_MAX_MS) {
      return;
    }
    this.isRefreshingRuns = true;
    this.runRefreshTimer = setTimeout(() => this.refreshRunStatuses(), RUN_REFRESH_MS);
  }

  private refreshRunStatuses(): void {
    this.runRefreshTimer = null;
    this.SubscriptionsService.getUserSubscriptions().subscribe({
      next: (data) => {
        this.subscriptions = data;
      },
      // Keep the list as it is and try again on the next tick
      error: () => this.scheduleRunRefresh()
    });
  }

  private stopRunRefresh(): void {
    if (this.runRefreshTimer) {
      clearTimeout(this.runRefreshTimer);
      this.runRefreshTimer = null;
    }
    this.isRefreshingRuns = false;
  }

  private announceFinishedRuns(previous: Subscription[], next: Subscription[]): void {
    const wasRunning = new Set(previous.filter(sub => runStatusOf(sub) === 'RUNNING').map(sub => sub.subscriptionId));
    const finished = next.filter(sub => wasRunning.has(sub.subscriptionId) && runStatusOf(sub) !== 'RUNNING');
    if (finished.length > 0) {
      this.runStatusAnnouncement = finished
        .map(sub => `${sub.subscriptionName || sub.title}: ${runStatusView(sub).label}`)
        .join('. ');
    }
  }

  ngOnInit() {
    // Show success message passed via router state (e.g. after edit redirect)
    const navState = history.state;
    if (navState?.successMessage) {
      this.showNotification(navState.successMessage, 'success');
    }

    this.isLoading = true;

    this.SubscriptionsService.getUserSubscriptions().subscribe({
      next: (data) => {
        this.subscriptions = data;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  onSubscriptionClick(subscription: Subscription) {
    this.SubscriptionsService.setSelectedSubscription(subscription);
    const baseRoute = this.authContextService.hasInternalAccess ? '/internal/subscriptions' : '/subscriptions';
    this.router.navigate([baseRoute, this.SubscriptionsService.generateSubscriptionUriFromTitle(subscription)]);
  }

  onDownloadClick(storageURL: string, subscription: Subscription) {
    // Obfuscate sensitive data in the URL before sending to RUM
    const obfuscatedUrl = this.obfuscateUrl(storageURL);
    
    this.cloudWatchRumService.addCustomEvent('subscriptionDownload', {
      subscriptionId: subscription.subscriptionId,
      dashboardId: subscription.dashboardId,
      subscriptionUrl: obfuscatedUrl
    });
    
    this.SubscriptionsService.downloadFile(storageURL);
  }

  /**
   * Obfuscate AWS credentials in S3 Pre-Signed URLs
   * Masks sensitive AWS parameters like credentials, signatures, and session tokens
   * @param url The S3 Pre-Signed URL to obfuscate
   * @returns The obfuscated URL with sensitive AWS data masked
   */
  private obfuscateUrl(url: string): string {
    // Obfuscate AWS S3 Pre-Signed URL parameters that contain sensitive credentials
    return url
      // Mask X-Amz-Credential (contains AWS access key and other sensitive info)
      .replace(/([?&])(X-Amz-Credential)=([^&]*)/gi, '$1$2=***')
      // Mask X-Amz-Signature (the request signature)
      .replace(/([?&])(X-Amz-Signature)=([^&]*)/gi, '$1$2=***')
      // Mask X-Amz-Security-Token (session token if using temporary credentials)
      .replace(/([?&])(X-Amz-Security-Token)=([^&]*)/gi, '$1$2=***')
      // Also mask any other potentially sensitive parameters
      .replace(/([?&])(authorization|token|secret|key|password)=([^&]*)/gi, '$1$2=***');
  }

  onViewReportClick(subscription: Subscription) {
    // Navigate to the embedded dashboard using the subscription's dashboardId
  
    if (subscription.dashboardId) {
      const baseRoute = this.authContextService.hasInternalAccess ? '/internal/embedded-dashboard' : '/embedded-dashboard';
      this.router.navigate([baseRoute, subscription.dashboardId], {
        queryParams: { c: btoa(JSON.stringify(subscription)), contentId: subscription.contentid },
      });
    } else {
      console.warn('No dashboardId available for subscription:', subscription);
    }
  }

  /**
   * Format timestamp for display in Central time
   * @param timestamp UTC timestamp from API
   * @returns Formatted string in Central time
   */
  formatTimestamp(timestamp: string): string {
    return utcToCentralDisplay(timestamp, 'full');
  }

  onEditClick(subscription: Subscription) {
    this.SubscriptionsService.setSelectedSubscription(subscription);
    const baseRoute = this.authContextService.hasInternalAccess ? '/internal/edit-subscription' : '/edit-subscription';
    this.router.navigate([baseRoute], {
      queryParams: { pageMode: 'edit' }
    });
  }
  onOverflowItemSelected(event: Event, sub: Subscription) {
    // TODO: Add cloudwatch rum call here:

    // this.cloudWatchRumService.addCustomEvent('reportsAndDashboardsItemSelected', { payload });
    switch ((event as CustomEvent).detail.action) {
      case 'delete':
        this.SubscriptionsService.deleteSubscription(sub).subscribe({
          next: (response) => {
            this.recordSubscriptionDeleted(sub);
            this.subscriptions = this.subscriptions.filter((subscription) => subscription.subscriptionId !== sub.subscriptionId);
            this.showNotification('Subscription deleted successfully.', 'success');
          },
          error: (error) => {
            console.error('Error deleting subscription', error);
            this.showNotification('Failed to delete subscription. Please try again.', 'error');
          },
          complete: () => { }
        })
        break;
      case 'edit':
      default:
        this.onEditClick(sub);
        break;
    }
  }
  onOverflowItemClicked(event: any, sub: Subscription) {
    switch (event.name) {
      case 'Delete':
        this.isDeleteSubscriptionModalVisible = true;
        this.subscriptionToDelete = sub;
        break;
      case 'Edit':
      default:
        this.onEditClick(sub);
        break;
    }
  }
  onModalDeleteButtonClicked() {
    const subscriptionToDelete = this.subscriptionToDelete;
    if (!subscriptionToDelete) return;
            this.SubscriptionsService.deleteSubscription(subscriptionToDelete).subscribe({
          next: (response) => {
            this.recordSubscriptionDeleted(subscriptionToDelete);
            this.subscriptions = this.subscriptions.filter((subscription) => subscription.subscriptionId !== subscriptionToDelete.subscriptionId);
            this.showNotification('Subscription deleted successfully.', 'success');
          },
          error: (error) => {
            console.error('Error deleting subscription', error);
            this.showNotification('Failed to delete subscription. Please try again.', 'error');
            this.isDeleteSubscriptionModalVisible = false;
            this.subscriptionToDelete = null;
          },
          complete: () => { 
            this.isDeleteSubscriptionModalVisible = false;
            this.subscriptionToDelete = null;
          }
        })
    
  }
  private recordSubscriptionDeleted(subscription: Subscription): void {
    let savedForm: Record<string, unknown> = {};
    try {
      const parsed = JSON.parse(subscription.description || '{}');
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) savedForm = parsed;
    } catch {
      savedForm = {};
    }
    const readString = (...values: unknown[]): string | null => {
      for (const value of values) {
        if (typeof value === 'string' && value.trim() && value.trim().toLowerCase() !== 'null') return value.trim();
      }
      return null;
    };
    const contentId = readString(subscription.contentId, subscription.contentid, savedForm['contentId'], savedForm['contentid']);
    const frequency = readString(subscription.frequency, savedForm['frequency']);
    this.cloudWatchRumService.addCustomEvent('subscription_deleted', {
      subscriptionId: subscription.subscriptionId,
      dashboardId: subscription.dashboardId,
      contentId,
      frequency,
      format: subscription.format,
      missingMetadata: [!contentId ? 'contentId' : '', !frequency ? 'frequency' : ''].filter(Boolean),
    });
  }

  onModalDeleteCancelButtonClicked() {
    this.isDeleteSubscriptionModalVisible = false;
    this.subscriptionToDelete = null;
  }
}
